import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/** Top-level Pro shell screens — switching between these should not stack history. */
export const PRO_SHELL_ROOT_PATHS = new Set([
  '/pro/home',
  '/pro/workspaces',
  '/pro/notifications',
  '/pro/profile',
  '/pro/bills',
  '/pro/legislators',
  '/pro/calendar',
  '/pro/help',
]);

/**
 * Whether navigating from `from` to `to` should replace the current history
 * entry instead of pushing a new one.
 *
 * - Home → section: push (Back returns to Home)
 * - Section ↔ section: replace (Back skips repeated lateral hops)
 * - Detail → section: replace (Back returns to the list beneath the detail)
 */
export function shouldReplaceProShellNav(from: string, to: string): boolean {
  if (from === to) return false;
  if (!PRO_SHELL_ROOT_PATHS.has(to)) return false;
  if (from === '/pro/home') return false;
  if (PRO_SHELL_ROOT_PATHS.has(from)) return true;
  if (from.startsWith('/pro/')) return true;
  return false;
}

/** Navigate between Pro shell roots without accumulating lateral history. */
export function useProShellNavigate() {
  const navigate = useNavigate();
  const location = useLocation();

  return useCallback(
    (path: string) => {
      if (path === location.pathname) return;
      navigate(path, { replace: shouldReplaceProShellNav(location.pathname, path) });
    },
    [location.pathname, navigate],
  );
}
